import type { LocalDate } from '../dates';
import type { DomainEvent } from '../events';
import type { Mutation } from '../mutations';
import { MAX_AMOUNT_MINOR, MAX_NOTE_LENGTH } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import { cleanText, MAX_STORE_NAME } from './items';
import { normalizeItemName } from './prices';
import {
  shoppingMutation,
  type ShoppingItemLike,
  type ShoppingResult,
  type ShoppingTripItemLike,
  type ShoppingTripLike,
} from './types';

export type BuyError =
  | 'no_items'
  | 'item_not_open'
  | 'amount_required'
  | 'amount_too_large'
  | 'bad_price'
  | 'prices_over_total'
  | 'prices_mismatch'
  | 'store_too_long'
  | 'bad_date';

export interface BuyInput {
  /** The ticked items, in list order. */
  items: readonly ShoppingItemLike[];
  totalMinor: number;
  /** "Price each item": item id → centavos. Items left out have no price of their own. */
  prices?: Readonly<Record<string, number | null | undefined>>;
  store?: string | null;
  date: LocalDate;
  /**
   * The id the trip's Spending expense will have (the caller makes one while Spending is usable),
   * written on the trip insert so no later link update has to win over it. Null: no expense.
   */
  expenseId?: string | null;
  /** v0.5: the "From" account picked on the buy sheet (absent: the rule's default). */
  accountId?: string | null;
  /** v0.7.6: a card picked under "From" ('card:<payee id>'): a card purchase, no account. */
  method?: string | null;
}

/** Sum of the ticked items' estimates: what "How much?" starts with. */
export function sumEstimates(items: readonly Pick<ShoppingItemLike, 'estimateMinor'>[]): number {
  return items.reduce((s, i) => s + (i.estimateMinor ?? 0), 0);
}

/**
 * The trip total once per-item prices are in (spec §6: "their sum must equal the total, or the
 * total updates to match"). When every item has a price, the total is their sum; when some are
 * blank, the total stays what the user typed unless the prices already add up to more.
 */
export function reconcileTotal(
  totalMinor: number | null,
  itemIds: readonly string[],
  prices: Readonly<Record<string, number | null | undefined>>,
): number | null {
  const priced = itemIds.map((id) => prices[id]).filter((p): p is number => p != null && p > 0);
  const sum = priced.reduce((s, p) => s + p, 0);
  if (priced.length === itemIds.length && itemIds.length > 0) return sum;
  if (totalMinor == null) return sum > 0 ? sum : null;
  return Math.max(totalMinor, sum);
}

/** The expense note: the items bought, within Spending's note limit. */
export function tripNote(names: readonly string[]): string | null {
  const text = names.join(', ');
  if (text === '') return null;
  return text.length <= MAX_NOTE_LENGTH ? text : `${text.slice(0, MAX_NOTE_LENGTH - 1)}…`;
}

/**
 * Buying (spec §6): one trip with a line per item, the items leave the open list, and a
 * 'shopping.bought' event that the shopping.expense rule turns into one Spending expense.
 * A single item bought without its own price takes the total as its price (a price point).
 */
export function buildBuy(
  input: BuyInput,
  ctx: BuildCtx,
): ShoppingResult<{ tripId: string; mutations: Mutation[]; event: DomainEvent }, BuyError> {
  const items = input.items;
  if (items.length === 0) return { ok: false, error: 'no_items' };
  if (items.some((i) => i.deletedAt !== null || i.tripId !== null))
    return { ok: false, error: 'item_not_open' };
  const total = input.totalMinor;
  if (!Number.isInteger(total) || total <= 0) return { ok: false, error: 'amount_required' };
  if (total > MAX_AMOUNT_MINOR) return { ok: false, error: 'amount_too_large' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) return { ok: false, error: 'bad_date' };
  const store = cleanText(input.store);
  if (store && store.length > MAX_STORE_NAME) return { ok: false, error: 'store_too_long' };

  const prices = new Map<string, number>();
  for (const i of items) {
    const p = input.prices?.[i.id];
    if (p == null) continue;
    if (!Number.isInteger(p) || p <= 0 || p > MAX_AMOUNT_MINOR)
      return { ok: false, error: 'bad_price' };
    prices.set(i.id, p);
  }
  const sum = [...prices.values()].reduce((s, p) => s + p, 0);
  if (sum > total) return { ok: false, error: 'prices_over_total' };
  if (prices.size === items.length && sum !== total) return { ok: false, error: 'prices_mismatch' };
  if (items.length === 1 && prices.size === 0) prices.set(items[0]!.id, total);

  const tripId = ctx.newId();
  const tripInsert = shoppingMutation(ctx, 'shopping_trips', 'insert', tripId, {
    date: input.date,
    store,
    totalMinor: total,
    currency: 'PHP',
    expenseId: input.expenseId ?? null,
  });
  const mutations: Mutation[] = [tripInsert];
  items.forEach((i, idx) => {
    mutations.push(
      shoppingMutation(ctx, 'shopping_trip_items', 'insert', ctx.newId(), {
        tripId,
        itemId: i.id,
        name: i.name,
        normalizedName: normalizeItemName(i.name),
        quantity: i.quantity,
        unit: i.unit,
        priceMinor: prices.get(i.id) ?? null,
        position: idx + 1,
      }),
      shoppingMutation(ctx, 'shopping_items', 'update', i.id, { tripId }),
    );
  });
  const event: DomainEvent = {
    type: 'shopping.bought',
    tripId,
    totalMinor: total,
    date: input.date,
    store,
    note: tripNote(items.map((i) => i.name)),
    expenseId: input.expenseId ?? null,
    ...(input.method?.startsWith('card:')
      ? { method: input.method, accountId: null }
      : input.accountId !== undefined
        ? { accountId: input.accountId }
        : {}),
    at: ctx.now,
    mutationId: tripInsert.id,
  };
  return { ok: true, tripId, mutations, event };
}

/**
 * Undo right after buying: the trip, its lines and its expense are deleted (soft, kept in
 * History) and the items go back on the open list.
 */
export function buildUndoBuy(
  trip: ShoppingTripLike,
  lines: readonly Pick<ShoppingTripItemLike, 'id' | 'itemId'>[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    ...buildDeleteTrip(trip, lines, ctx),
    ...lines
      .filter((l) => l.itemId !== null)
      .map((l) => shoppingMutation(ctx, 'shopping_items', 'update', l.itemId!, { tripId: null })),
  ];
}

/** Deleting a past trip deletes its lines and its expense too (soft; History keeps them). */
export function buildDeleteTrip(
  trip: ShoppingTripLike,
  lines: readonly Pick<ShoppingTripItemLike, 'id'>[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    shoppingMutation(ctx, 'shopping_trips', 'delete', trip.id),
    ...lines.map((l) => shoppingMutation(ctx, 'shopping_trip_items', 'delete', l.id)),
    ...(trip.expenseId ? [shoppingMutation(ctx, 'ledger_entries', 'delete', trip.expenseId)] : []),
  ];
}

/** Undo of a trip delete: empty inserts on the soft-deleted ids (conventions §14). */
export function buildRestoreTrip(
  trip: ShoppingTripLike,
  lines: readonly Pick<ShoppingTripItemLike, 'id'>[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    shoppingMutation(ctx, 'shopping_trips', 'insert', trip.id, {}),
    ...lines.map((l) => shoppingMutation(ctx, 'shopping_trip_items', 'insert', l.id, {})),
    ...(trip.expenseId
      ? [shoppingMutation(ctx, 'ledger_entries', 'insert', trip.expenseId, {})]
      : []),
  ];
}
